/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { getLocaleString } from "utils/localization/locale";
import ListingTable from "components/ReactTable";
import get from "lodash/get";
import DateRangePicker from "components/DateRangePicker";
import Button from "components/Button";
import Dropdown from "components/Button/Dropdown";
import moment from "moment";
import { utcToLocal } from "utils/dates";
import omit from "lodash/omit";
import { setuxvalue, getuxvalue } from "handlers/ux";
import { convertAmountWithDecimal } from "utils";
import Toaster from "components/Toaster";
import { getBankFileList, uploadBankFile } from "./actions";

export const initialFilters = {
  page: 1,
  per_page: 10,
  to_date: moment().format("MM/DD/YYYY"),
  sort_by: "",
  interval: "",
  status: "",
  from_date: moment().subtract(1, "year").format("MM/DD/YYYY"),
  name: "",
  sort_order: "",
  file_type: "",
  format_type: "",
};

function BankFiles() {
  const [filters, setFilters] = useState(
    getuxvalue("BANK_FILES") || initialFilters
  );
  const [fileList, setFileList] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isFetchingFiles, setIsFetchingFiles] = useState(false);
  const [paginationParams, setPaginationParams] = useState({
    next_num: 1,
    prev_num: null,
  });

  const history = useHistory();

  const {
    page = 1,
    to_date = moment(),
    sort_by = "",
    interval,
    status = "",
    from_date = moment(),
    name = "",
    sort_order = "",
    file_type = "",
    format_type = "",
  } = filters;

  const { next_num, prev_num } = paginationParams;

  const updateFilters = (newFilters = {}) => {
    const finalFilters = {
      ...filters,
      ...newFilters,
    };
    setFilters(finalFilters);
    setuxvalue("BANK_FILES", finalFilters);
  };

  const setDates = (from_date, to_date, interval) =>
    updateFilters({
      from_date: from_date ? moment(from_date).format("MM/DD/YYYY") : "",
      to_date: to_date ? moment(to_date).format("MM/DD/YYYY") : "",
      interval,
    });

  const getBankFiles = useCallback((filters) => {
    setIsFetchingFiles(true);
    getBankFileList({
      ...omit(filters, ["interval", "next_num", "prev_num"]),
    })
      .then((res) => {
        setFileList(get(res, "data.result", []));
        setPaginationParams({
          next_num: get(res, "data.next_num", 1),
          prev_num: get(res, "data.prev_num", null),
        });
        setIsFetchingFiles(false);
      })
      .catch(() => {
        setIsFetchingFiles(false);
      });
  }, []);

  useEffect(() => {
    document.title = `${getLocaleString(
      "bank_files.title",
      "Bank Files"
    )} - ${getLocaleString("header.appName", "Serrala")}`;
  }, []);

  useEffect(() => {
    getBankFiles(filters);
  }, [page, sort_by, sort_order]);

  const Increment = () => updateFilters({ page: page + 1 });

  const Decrement = () => updateFilters({ page: page - 1 });

  const handleClick = (file) => {
    // Navigate to the bank file details page
    history.push(`/bank-files/${file.id}`, {
      selectedFile: file,
    });
  };

  // Helper function to check if amounts should be shown as "-"
  const shouldShowProcessing = (status) => status !== "PARSED";

  const columns = [
    {
      header: () => getLocaleString("common.sr_no", "Sr. No."),
      show: true,
      accessorKey: "id",
      id: "id",
      className: "ps-3",
      size: 50,
      dragable: false,
      cell: (data) => <div>{get(data, "row.index", 0) + 1}</div>,
    },
    {
      header: () => (
        <div className="cursor-pointer">
          {getLocaleString("bank_files.file_name", "File")}
          <span className="sort-icons">
            <i data-tlabel="sort list" className="fas fa-caret-up" />
            <i data-tlabel="sort list" className="fas fa-caret-down" />
          </span>
        </div>
      ),
      show: true,
      accessorKey: "name",
      id: "name",
      size: 220,
      dragable: false,
      cell: (data) => {
        const status = get(data, "row.original.status");
        return (
          <div>
            {data.getValue()}
            <div>
              {getLocaleString("common.status", "Status")}: {status}
            </div>
          </div>
        );
      },
    },
    {
      header: () => (
        <div className="cursor-pointer">
          {getLocaleString("bank_files.dates", "Dates")}
          <span className="sort-icons">
            <i data-tlabel="sort list" className="fas fa-caret-up" />
            <i data-tlabel="sort list" className="fas fa-caret-down" />
          </span>
        </div>
      ),
      show: true,
      id: "uploaded_at",
      accessorKey: "uploaded_at",
      dragable: false,
      size: 180,
      cell: (data) => {
        const uploadedAt = data.getValue();
        const paymentDate = get(data, "row.original.payment_date");

        return (
          <div className="text-xs">
            {paymentDate && (
              <div>
                <strong>
                  {getLocaleString("cash_remittance.pymt_date", "Pymt Date")}:{" "}
                  {moment(paymentDate).format("DD, MMM YYYY, ddd")}
                </strong>
              </div>
            )}
            <div>
              {getLocaleString("bank_files.upload", "Upload")}:{" "}
              {uploadedAt ? utcToLocal(data.getValue()) : "-"}
            </div>
          </div>
        );
      },
    },
    {
      header: () => (
        <div>{getLocaleString("bank_files.file_format", "File Format")}</div>
      ),
      show: true,
      id: "file_format",
      accessorKey: "format_type",
      dragable: false,
      size: 80,
      cell: (data) => {
        const formatType = data.getValue();
        const fileType = get(data, "row.original.file_type");
        return (
          <div>
            {formatType && fileType
              ? `${formatType} (${fileType})`
              : formatType || fileType || "-"}
          </div>
        );
      },
    },
    {
      header: () => (
        <div>
          {getLocaleString("bank_files.total_line_amount", "Total Line Amount")}
        </div>
      ),
      show: true,
      accessorKey: "total_line_amount",
      id: "total_line_amount",
      dragable: false,
      size: 150,
      cell: (data) => {
        const amount = data.getValue();
        const lineCount = get(data, "row.original.total_line_count");
        const status = get(data, "row.original.status");

        // For non-PARSED status, show "-" instead of amount
        if (shouldShowProcessing(status)) {
          return (
            <div>
              <span className="text-muted">-</span>
            </div>
          );
        }

        const amountDisplay =
          amount !== null && amount !== undefined
            ? convertAmountWithDecimal(amount)
            : "-";

        const lineCountDisplay =
          lineCount !== null && lineCount !== undefined
            ? `(${lineCount} lines)`
            : "";

        return (
          <div>
            <div>{amountDisplay}</div>
            {lineCountDisplay && (
              <small className="text-muted">{lineCountDisplay}</small>
            )}
          </div>
        );
      },
    },
    {
      header: () => (
        <div>
          {getLocaleString(
            "bank_files.linkable_line_amount_total",
            "Linkable Line Amount"
          )}
        </div>
      ),
      show: true,
      accessorKey: "linkable_line_amount_total",
      id: "linkable_line_amount_total",
      dragable: false,
      size: 150,
      cell: (data) => {
        const status = get(data, "row.original.status");
        const lineCount = get(data, "row.original.linkable_line_count");

        // For non-PARSED status, show "-" instead of amount
        if (shouldShowProcessing(status)) {
          return (
            <div>
              <span className="text-muted">-</span>
            </div>
          );
        }

        const lineCountDisplay =
          lineCount !== null && lineCount !== undefined
            ? `(${lineCount} lines)`
            : "";

        return (
          <div className="text-danger">
            <div>
              <strong>
                {data.getValue() !== null && data.getValue() !== undefined
                  ? convertAmountWithDecimal(data.getValue())
                  : "-"}
              </strong>
            </div>
            {lineCountDisplay && <small>{lineCountDisplay}</small>}
          </div>
        );
      },
    },
    {
      header: () => (
        <div>
          {getLocaleString(
            "bank_files.total_linked_amount",
            "Total Linked Amount"
          )}
        </div>
      ),
      show: true,
      accessorKey: "total_linked_amount",
      id: "total_linked_amount",
      dragable: false,
      size: 150,
      cell: (data) => {
        const status = get(data, "row.original.status");

        // For non-PARSED status, show "-" instead of amount
        if (shouldShowProcessing(status)) {
          return (
            <div>
              <span className="text-muted">-</span>
            </div>
          );
        }

        return (
          <div className="text-success">
            <strong>
              {data.getValue() !== null && data.getValue() !== undefined
                ? convertAmountWithDecimal(data.getValue())
                : "-"}
            </strong>
          </div>
        );
      },
    },
    {
      header: () => <div />,
      show: true,
      id: "action",
      accessorKey: "status",
      dragable: false,
      size: 60,
      disableClick: true,
      cell: (data) => (
        <div>
          {get(data, "row.original.presigned_url") ? (
            <i
              title={getLocaleString(
                "bank_files.download_file",
                "Download File"
              )}
              className="fas fa-download text-secondary cursor-pointer"
              onClick={() => {
                const link = document.createElement("a");
                link.href = get(data, "row.original.presigned_url");
                link.download = get(data, "row.original.name");
                link.target = "_blank";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            />
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="row px-15" id="dashboardBody">
      <div className="col-md-12">
        <div className="card">
          <div className="card-body">
            <div className="row">
              <div className="col-md-12 text-center mb-4">
                <h5>{getLocaleString("bank_files.title", "Bank Files")}</h5>
              </div>
              <div className="col-md-12">
                <div className="dropdown d-inline-block mb-3 me-2">
                  <Dropdown
                    showDropdown={showFilter}
                    buttonProps={{
                      as: "span",
                      role: "button",
                      "data-toggle": "dropdown",
                      "aria-haspopup": "true",
                      "aria-expanded": "false",
                    }}
                    btnClassName="dropdown-toggle cursor-pointer"
                    setShowDropdown={() => setShowFilter(!showFilter)}
                    buttonText={getLocaleString("common.filter", "Filter")}
                    dropdownWrapperClassName="filterdroplist p-2 pe-4 width-500px"
                    dropdownWrapperProps={{
                      id: "outerside",
                    }}
                    filterButton
                  >
                    <form id="filter-form" className="dropdown-wrap">
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label>
                            {getLocaleString(
                              "common.upload_date",
                              "Upload Date"
                            )}
                          </label>
                          <div className="single-date-picker t-12 overflow-hidden">
                            <div className="t-12">
                              <DateRangePicker
                                startDate={from_date ? moment(from_date) : ""}
                                label={interval}
                                endDate={to_date ? moment(to_date) : ""}
                                isClearable={from_date || to_date}
                                getSelectedDates={setDates}
                                parentEl="#outerside"
                                allToDate={true}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <label>
                            {getLocaleString(
                              "bank_files.file_name",
                              "File Name"
                            )}
                          </label>
                          <input
                            type="text"
                            defaultValue={name}
                            onChange={(e) =>
                              updateFilters({
                                name: e.target.value,
                              })
                            }
                            className="form-control t-14"
                            placeholder={getLocaleString(
                              "bank_files.file_name",
                              "File Name"
                            )}
                          />
                        </div>
                        <div className="col-md-6">
                          <label>
                            {getLocaleString(
                              "bank_files.file_type",
                              "File Type"
                            )}
                          </label>
                          <select
                            className="form-control w-100"
                            value={file_type}
                            onChange={(e) =>
                              updateFilters({ file_type: e.target.value })
                            }
                          >
                            <option value="">
                              {getLocaleString("common.all", "All")}
                            </option>
                            <option value="TXT">TXT</option>
                            <option value="IMAGE">IMAGE</option>
                            <option value="PDF">PDF</option>
                            <option value="XML">XML</option>
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label>
                            {getLocaleString(
                              "bank_files.format_type",
                              "Format Type"
                            )}
                          </label>
                          <select
                            className="form-control w-100"
                            value={format_type}
                            onChange={(e) =>
                              updateFilters({ format_type: e.target.value })
                            }
                          >
                            <option value="">
                              {getLocaleString("common.all", "All")}
                            </option>
                            <option value="BAI2">BAI2</option>
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label>
                            {getLocaleString("common.status", "Status")}
                          </label>
                          <select
                            className="form-control w-100"
                            value={status}
                            onChange={(e) =>
                              updateFilters({ status: e.target.value })
                            }
                          >
                            <option value="">
                              {getLocaleString("common.all", "All")}
                            </option>
                            <option value="PENDING">
                              {getLocaleString("bank_files.pending", "Pending")}
                            </option>
                            <option value="PROCESSING">
                              {getLocaleString(
                                "bank_files.processing",
                                "Processing"
                              )}
                            </option>
                            <option value="PARSED">
                              {getLocaleString("bank_files.parsed", "Parsed")}
                            </option>
                            <option value="INVOICE_DETECTION">
                              {getLocaleString(
                                "bank_files.invoice_detection",
                                "Invoice Detection"
                              )}
                            </option>
                            <option value="PROCESSED">
                              {getLocaleString(
                                "bank_files.processed",
                                "Processed"
                              )}
                            </option>
                            <option value="ERROR">
                              {getLocaleString("bank_files.error", "Error")}
                            </option>
                          </select>
                        </div>
                        <div className="col-md-6" />
                        <div className="col-md-12 mt-2">
                          <div className="setbtn d-flex justify-content-end box-space">
                            <Button
                              type="button"
                              variant="outline-primary"
                              className="resetbtn me-1 min-width25"
                              data-cy="resetbtn"
                              onClick={() => {
                                setShowFilter(false);
                                updateFilters(initialFilters);
                                getBankFiles({ ...initialFilters });
                              }}
                            >
                              {getLocaleString("common.reset", "Reset")}
                            </Button>
                            <Button
                              type="button"
                              className="applybtn w-25"
                              data-cy="applybtn"
                              onClick={() => {
                                setShowFilter(false);
                                getBankFiles({ ...filters });
                              }}
                            >
                              {getLocaleString("common.submit", "Submit")}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </form>
                  </Dropdown>
                </div>

                <div className="d-inline-block mb-3 me-2">
                  <Button
                    className={`cursor-pointer
                      mb-0 ${isUploading ? "disabled" : ""}`}
                    as="label"
                    data-tlabel="Upload bank file"
                    startIcon={<i className="fa fa-upload me-1" />}
                  >
                    <input
                      id="upload_bank_file"
                      type="file"
                      className="d-none"
                      onChange={(e) => {
                        setIsUploading(true);
                        uploadBankFile(e)
                          .then((res) => {
                            if (res) {
                              Toaster(
                                getLocaleString(
                                  "common.file_upload_success",
                                  "Bank file uploaded successfully"
                                ),
                                "success"
                              );
                              getBankFiles({ ...filters });
                              setIsUploading(false);
                            }
                          })
                          .catch(() => {
                            setIsUploading(false);
                          });
                      }}
                      accept=".csv, .xlsx, .xls, .txt"
                    />
                    {getLocaleString(
                      "bank_files.upload_bank_file",
                      "Upload Bank File"
                    )}
                    {isUploading ? (
                      <i className="ms-1 fa fa-spinner fa-spin" />
                    ) : null}
                  </Button>
                </div>

                <ListingTable
                  minRows={fileList.length > 20 ? 1 : fileList?.length}
                  isFetching={isFetchingFiles}
                  columns={columns}
                  data={fileList}
                  currPage={page}
                  onRowClick={(rowInfo) => handleClick(rowInfo)}
                  sorting={
                    sort_by
                      ? [
                          {
                            id: sort_by,
                            desc: sort_order === "desc",
                          },
                        ]
                      : []
                  }
                  nextPage={next_num}
                  prevPage={prev_num}
                  setNextPage={Increment}
                  setPrevPage={Decrement}
                  noDataText={
                    isFetchingFiles
                      ? ""
                      : getLocaleString("common.nodataFound", "No data found")
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  clientDetails: state.clientDetails,
});

export default connect(mapStateToProps, null)(BankFiles);
